import React, { useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";

const LiveVideos = () => {

  const [videos, setVideos] = useState([]);
  const [premium, setPremium] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [now, setNow] = useState(Date.now()); // 🔥 live clock

  const navigate = useNavigate();

  // ================= LIVE CLOCK (updates every second) =================
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ================= CHECK PREMIUM =================
  useEffect(() => {
    const uid = localStorage.getItem("uid");

    if (!uid) {
      setPremium(false);
      return;
    }

    get(ref(db, "users/" + uid)).then((snap) => {
      if (snap.exists()) {
        setPremium(snap.val().is_premium === true);
      }
    });
  }, []);

  // ================= THUMBNAIL HELPER =================
  const getThumbnail = (videoUrl, thumbUrl) => {

    if (thumbUrl && thumbUrl.includes("drive.google.com")) {
      let fileId = "";
      const match1 = thumbUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
      const match2 = thumbUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);

      if (match1) fileId = match1[1];
      else if (match2) fileId = match2[1];

      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
      }
    }

    if (thumbUrl) return thumbUrl;

    if (videoUrl && (videoUrl.includes("youtube") || videoUrl.includes("youtu.be"))) {
      let id = "";

      if (videoUrl.includes("watch?v="))
        id = videoUrl.split("watch?v=")[1].split("&")[0];
      else if (videoUrl.includes("youtu.be/"))
        id = videoUrl.split("youtu.be/")[1];

      if (id) {
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      }
    }

    return "/fallback.jpg";
  };

  // ================= FETCH VIDEOS =================
  useEffect(() => {

  const fetchLiveVideos = async () => {

    try {

      const snapshot = await get(ref(db, "videos/lectures"));

      if (!snapshot.exists()) {
        setVideos([]);
        return;
      }

      const data = snapshot.val();
      const currentTime = Date.now();

      const list = Object.keys(data).map(id => {

        const v = data[id];
        const start = v.liveStartTime
          ? new Date(v.liveStartTime).getTime()
          : null;

        let status = "normal";

        if (start) {
          const end = start + (3 * 60 * 60 * 1000);

          if (currentTime < start) {
            status = "upcoming";
          } else if (currentTime <= end) {
            status = "live";
          }
        }

        return {
          id,
          title: v.title || "No Title",
          videoUrl: v.videoUrl || v.vimeoUrl,
          thumbnailUrl: getThumbnail(
            v.videoUrl || v.vimeoUrl,
            v.thumbnailUrl
          ),
          liveStartTime: start,
          status
        };
      });

      // 🔥 ANDROID STYLE SORTING
list.sort((a, b) => {

  // 1️⃣ Upcoming FIRST (nearest start time first)
  if (a.status === "upcoming" && b.status !== "upcoming") return -1;
  if (b.status === "upcoming" && a.status !== "upcoming") return 1;

  // If both upcoming → nearest one first
  if (a.status === "upcoming" && b.status === "upcoming") {
    return (a.liveStartTime || 0) - (b.liveStartTime || 0);
  }

  // 2️⃣ Then LIVE
  if (a.status === "live" && b.status !== "live") return -1;
  if (b.status === "live" && a.status !== "live") return 1;

  // 3️⃣ Then old videos (latest first)
  return (b.liveStartTime || 0) - (a.liveStartTime || 0);
});

      setVideos(list);

    } catch (error) {
      console.log("Error loading live videos:", error);
    }
  };

  fetchLiveVideos();

  const interval = setInterval(fetchLiveVideos, 60000);
  return () => clearInterval(interval);

}, []);

  // ================= OPEN VIDEO =================
  const openVideo = (url) => {

    if (!url) return;

    if (!premium) {
      setShowPremium(true);
      return;
    }

    if (url.includes("youtube") || url.includes("youtu.be")) {
      window.open(url, "_blank");
      return;
    }

    navigate("/player", { state: { url } });
  };

  const handleBuyPremium = () => {
    setShowPremium(false);
    navigate("/counselling-program");
  };

  return (
    <div style={{ padding: "20px" }}>
      <h3 style={{ marginBottom: "20px" }}>Live Lectures</h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "20px"
        }}
      >
        {videos.map(video => (
          <VideoCard
            key={video.id}
            video={video}
            openVideo={openVideo}
            now={now}
          />
        ))}
      </div>

      {showPremium && (
        <div style={backdropStyle}>
          <div style={modalStyle}>
            <h3>Premium Access Required</h3>
            <p>Enroll for Counselling Program to access this content.</p>
            <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              <button onClick={() => setShowPremium(false)}>CANCEL</button>
              <button onClick={handleBuyPremium}>ENROLL NOW</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ================= VIDEO CARD =================
function VideoCard({ video, openVideo, now }) {

  const [imageError, setImageError] = useState(false);

  // liveStartTime is already a NUMBER (timestamp)
  const start = typeof video.liveStartTime === "number"
    ? video.liveStartTime
    : null;

  const end = start ? start + (3 * 60 * 60 * 1000) : null;

  const isUpcoming = start && now < start;
  const isLive = start && now >= start && now <= end;

  const getCountdown = () => {
    if (!isUpcoming || !start) return null;

    const diff = start - now;
    if (diff <= 0) return "🔴 LIVE NOW";

    const totalSec = Math.floor(diff / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (hours > 0) {
      return `LIVE IN ${hours}h ${minutes}m`;
    }

    return `LIVE IN ${minutes}m ${seconds}s`;
  };

  return (
    <div
      onClick={() => {
        if (isUpcoming) {
          alert("Live starts soon");
          return;
        }
        openVideo(video.videoUrl);
      }}
      style={{
        borderRadius: "18px",
        overflow: "hidden",
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        cursor: isUpcoming ? "not-allowed" : "pointer",
        background: "#fff",
        opacity: isUpcoming ? 0.85 : 1,
        transition: "0.3s ease"
      }}
    >
      <div style={{ position: "relative" }}>

        {(isUpcoming || isLive) && (
          <div style={liveBadgeStyle}>
            {isUpcoming ? "LIVE SOON" : "🔴 LIVE"}
          </div>
        )}

        {!imageError ? (
          <img
            src={video.thumbnailUrl}
            alt=""
            style={{
              width: "100%",
              height: "180px",
              objectFit: "cover"
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          <div style={fallbackStyle}>
            {video.title}
          </div>
        )}
      </div>

      <div style={{
        padding: "12px",
        fontWeight: "600",
        fontSize: "15px"
      }}>
        {video.title}
      </div>

      {(isUpcoming || isLive) && (
        <div style={countdownStyle}>
          {isUpcoming ? getCountdown() : "🔴 LIVE NOW"}
        </div>
      )}
    </div>
  );
}


// ================= STYLES =================
const liveBadgeStyle = {
  position: "absolute",
  top: "10px",
  left: "10px",
  background: "#ff0000",
  color: "#fff",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "600"
};

const countdownStyle = {
  padding: "0 12px 12px",
  fontWeight: "600",
  color: "#d32f2f",
  fontSize: "13px"
};

const fallbackStyle = {
  height: "180px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #4f46e5, #06b6d4)",
  color: "#fff",
  fontWeight: "600"
};

const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center"
};

const modalStyle = {
  background: "#fff",
  padding: "25px",
  borderRadius: "10px",
  width: "300px",
  textAlign: "center"
};

export default LiveVideos;